// CalendarView.js - Enhanced Calendar Component for Time Tracking
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  Calendar, 
  Target, 
  ChevronLeft, 
  ChevronRight, 
  Edit2, 
  Copy, 
  Trash2, 
  Save, 
  X, 
  Plus, 
  Square, 
  List,
  Grid,
  Building2,
  DollarSign,
  User,
  MoreHorizontal
} from 'lucide-react';

// Calendar View Mode Selector Component
export const CalendarViewModeSelector = ({ viewMode, onViewModeChange, className = "" }) => {
  const viewModes = [
    { mode: 'day', icon: Calendar, label: 'Day' },
    { mode: 'week', icon: Square, label: 'Week' },
    { mode: 'month', icon: Grid, label: 'Month' }
  ];

  return (
    <div className={`flex items-center bg-slate-100 rounded-lg p-1 ${className}`}>
      {viewModes.map(({ mode, icon: Icon, label }) => (
        <button
          key={mode}
          onClick={() => onViewModeChange(mode)}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors ${
            viewMode === mode
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium">{label}</span>
        </button>
      ))}
    </div>
  );
};

// New Entry Modal Component
const NewEntryModal = ({ 
  selectedDate, 
  clients, 
  projects, 
  onClose, 
  onCreateEntry 
}) => {
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    task_name: '',
    description: '',
    duration_hours: '',
    is_billable: true,
    entry_date: selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Reset project when client changes
  useEffect(() => {
    if (formData.client_id && formData.project_id) {
      const projectExists = getProjectsForClient(formData.client_id).some(p => p.id === formData.project_id);
      if (!projectExists) {
        setFormData(prev => ({ ...prev, project_id: '' }));
      }
    }
  }, [formData.client_id]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.client_id) errors.client_id = 'Please select a client';
    if (!formData.project_id) errors.project_id = 'Please select a project';
    if (!formData.task_name.trim()) errors.task_name = 'Please enter a task name';
    if (!formData.duration_hours || parseFloat(formData.duration_hours) <= 0) {
      errors.duration_hours = 'Please enter a valid duration';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onCreateEntry({
      ...formData,
      duration_hours: parseFloat(formData.duration_hours)
    });
    onClose();
  };

  const getProjectsForClient = (clientId) => {
    return projects.filter(p => p.client_id === clientId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-800">Add Time Entry</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
       
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date *</label>
            <input
              type="date"
              value={formData.entry_date}
              onChange={(e) => setFormData(prev => ({ ...prev, entry_date: e.target.value }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client *</label>
              <select
                value={formData.client_id}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, client_id: e.target.value, project_id: '' }));
                  setValidationErrors(prev => ({ ...prev, client_id: '', project_id: '' }));
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                  validationErrors.client_id ? 'border-red-300' : 'border-slate-300'
                }`}
                required
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              {validationErrors.client_id && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.client_id}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Project *</label>
              <select
                value={formData.project_id}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, project_id: e.target.value }));
                  setValidationErrors(prev => ({ ...prev, project_id: '' }));
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                  validationErrors.project_id ? 'border-red-300' : 'border-slate-300'
                }`}
                disabled={!formData.client_id}
                required
              >
                <option value="">Select a project</option>
                {getProjectsForClient(formData.client_id).map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
              {validationErrors.project_id && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.project_id}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Task Name *</label>
            <input
              type="text"
              value={formData.task_name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, task_name: e.target.value }));
                setValidationErrors(prev => ({ ...prev, task_name: '' }));
              }}
              placeholder="What did you work on?"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationErrors.task_name ? 'border-red-300' : 'border-slate-300'
              }`}
              required
            />
            {validationErrors.task_name && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.task_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Duration (hours) *</label>
            <input
              type="number"
              value={formData.duration_hours}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, duration_hours: e.target.value }));
                setValidationErrors(prev => ({ ...prev, duration_hours: '' }));
              }}
              placeholder="2.5"
              min="0"
              step="0.25"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationErrors.duration_hours ? 'border-red-300' : 'border-slate-300'
              }`}
              required
            />
            {validationErrors.duration_hours && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.duration_hours}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add details about your work..."
              rows={3}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          
          <div>
            <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <input
                id="calendar-billable-checkbox"
                type="checkbox"
                checked={formData.is_billable}
                onChange={(e) => setFormData(prev => ({ ...prev, is_billable: e.target.checked }))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <label 
                htmlFor="calendar-billable-checkbox" 
                className="text-sm font-medium text-slate-700 cursor-pointer"
              >
                This time is billable
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Entry</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Entry Modal Component
const EditEntryModal = ({ 
  entry,
  clients, 
  projects, 
  onClose, 
  onSave 
}) => {
  const [editValues, setEditValues] = useState({
    task_name: entry.task_name,
    duration_hours: entry.duration_hours,
    description: entry.description || '',
    is_billable: entry.is_billable,
    client_id: entry.client_id,
    project_id: entry.project_id
  });

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!editValues.task_name.trim()) errors.task_name = 'Please enter a task name';
    if (!editValues.duration_hours || parseFloat(editValues.duration_hours) <= 0) {
      errors.duration_hours = 'Please enter a valid duration';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    onSave(entry.id, {
      ...editValues,
      duration_hours: parseFloat(editValues.duration_hours)
    });
    onClose();
  };

  const handleCancel = () => {
    setEditValues({
      task_name: entry.task_name,
      duration_hours: entry.duration_hours,
      description: entry.description || '',
      is_billable: entry.is_billable,
      client_id: entry.client_id,
      project_id: entry.project_id
    });
    onClose();
  };

  const getProjectsForClient = (clientId) => {
    return projects.filter(p => p.client_id === clientId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-800">Edit Time Entry</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client</label>
              <select
                value={editValues.client_id}
                onChange={(e) => {
                  setEditValues(prev => ({ ...prev, client_id: e.target.value, project_id: '' }));
                }}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Project</label>
              <select
                value={editValues.project_id}
                onChange={(e) => setEditValues(prev => ({ ...prev, project_id: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={!editValues.client_id}
              >
                <option value="">Select a project</option>
                {getProjectsForClient(editValues.client_id).map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Task Name *</label>
            <input
              type="text"
              value={editValues.task_name}
              onChange={(e) => {
                setEditValues(prev => ({ ...prev, task_name: e.target.value }));
                setValidationErrors(prev => ({ ...prev, task_name: '' }));
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationErrors.task_name ? 'border-red-300' : 'border-slate-300'
              }`}
            />
            {validationErrors.task_name && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.task_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Duration (hours) *</label>
            <input
              type="number"
              value={editValues.duration_hours}
              onChange={(e) => {
                setEditValues(prev => ({ ...prev, duration_hours: e.target.value }));
                setValidationErrors(prev => ({ ...prev, duration_hours: '' }));
              }}
              min="0"
              step="0.25"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationErrors.duration_hours ? 'border-red-300' : 'border-slate-300'
              }`}
            />
            {validationErrors.duration_hours && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.duration_hours}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              value={editValues.description}
              onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <input
                id="edit-billable-checkbox"
                type="checkbox"
                checked={editValues.is_billable}
                onChange={(e) => setEditValues(prev => ({ ...prev, is_billable: e.target.checked }))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <label 
                htmlFor="edit-billable-checkbox" 
                className="text-sm font-medium text-slate-700 cursor-pointer"
              >
                This time is billable
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
            <button
              onClick={handleCancel}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Time Entry Card Component for calendar views
const TimeEntryCard = ({ 
  entry, 
  clients, 
  projects, 
  onEdit, 
  onDuplicate, 
  onDelete,
  compact = false 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const client = clients?.find(c => c.id === entry.client_id) || { name: 'Unknown Client' };
  const project = projects?.find(p => p.id === entry.project_id) || { name: 'Unknown Project', color: '#6B7280' };

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;

  const revenue = entry.is_billable ? (entry.duration_hours * (entry.hourly_rate || project.hourly_rate || 0)) : 0;

  if (compact) {
    return (
      <div 
        className="p-2 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer"
        style={{ borderLeft: `3px solid ${project.color}` }}
        onClick={() => setShowActions(!showActions)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-800 truncate">{entry.task_name}</p>
            <p className="text-xs text-slate-600">{formatTime(entry.duration_hours)}</p>
          </div>
          {entry.is_billable && (
            <DollarSign className="w-3 h-3 text-green-600" />
          )}
        </div>
        
        {showActions && (
          <div className="flex items-center space-x-1 mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit(entry);
              }}
              className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate && onDuplicate(entry);
              }}
              className="p-1 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
            >
              <Copy className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(entry.id);
              }}
              className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all duration-200"
      style={{ borderLeft: `4px solid ${project.color}` }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            <h4 className="font-medium text-slate-900 truncate">{entry.task_name}</h4>
            {entry.is_billable && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <DollarSign className="w-3 h-3 mr-1" />
                Billable
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-slate-600 mb-2">
            <div className="flex items-center space-x-1">
              <Building2 className="w-4 h-4" />
              <span>{client.name}</span>
            </div>
            <div className="flex items-center space-x-1">
              <span>•</span>
              <span>{project.name}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{formatTime(entry.duration_hours)}</span>
            </div>
            {entry.is_billable && revenue > 0 && (
              <div className="flex items-center space-x-1">
                <DollarSign className="w-4 h-4" />
                <span>{formatCurrency(revenue)}</span>
              </div>
            )}
          </div>
          
          {entry.description && (
            <p className="text-sm text-slate-500 mt-2">{entry.description}</p>
          )}
        </div>
        
        <div className="flex items-center space-x-1 ml-4">
          <button
            onClick={() => onEdit && onEdit(entry)}
            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit entry"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDuplicate && onDuplicate(entry)}
            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Duplicate entry"
          >
            <Copy className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDelete && onDelete(entry.id)}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete entry"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Day View Component
const DayView = ({ 
  date, 
  entries, 
  progress, 
  onEntryEdit, 
  onEntryDuplicate, 
  onEntryDelete,
  onAddEntry,
  clients,
  projects 
}) => {
  return (
    <div className="space-y-4">
      {/* Daily Progress */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-slate-800">Daily Progress</span>
          </div>
          <span className="text-sm text-slate-600">
            {progress.actual.toFixed(1)}h / {progress.goal}h
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progress.percentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Add Entry Button */}
      <button
        onClick={() => onAddEntry(date)}
        className="w-full p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-all"
      >
        <Plus className="w-5 h-5 mx-auto mb-1" />
        <div className="text-sm">Add time entry</div>
      </button>

      {/* Time Entries */}
      <div className="space-y-3">
        {entries.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2" />
            <p>No time entries for this day</p>
          </div>
        ) : (
          entries.map((entry) => (
            <TimeEntryCard
              key={entry.id}
              entry={entry}
              clients={clients}
              projects={projects}
              onEdit={onEntryEdit}
              onDuplicate={onEntryDuplicate}
              onDelete={onEntryDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Week View Component
const WeekView = ({ 
  dates, 
  timeEntries, 
  getEntriesForDate,
  getTotalHoursForDate,
  isToday,
  onAddEntry,
  onEntryEdit,
  onEntryDuplicate,
  onEntryDelete,
  clients,
  projects,
  goals 
}) => {
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="grid grid-cols-7 gap-4">
      {dates.map((date, index) => {
        const entries = getEntriesForDate(date);
        const totalHours = getTotalHoursForDate(date);
        const isCurrentDay = isToday(date);
        const progress = (totalHours / goals.dailyHours) * 100;

        return (
          <div
            key={index}
            className={`border rounded-lg p-3 min-h-[200px] transition-all ${
              isCurrentDay 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            {/* Day Header */}
            <div className="mb-3">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${
                  isCurrentDay ? 'text-blue-700' : 'text-slate-600'
                }`}>
                  {weekDays[date.getDay()]}
                </span>
                <span className={`text-lg font-semibold ${
                  isCurrentDay ? 'text-blue-700' : 'text-slate-900'
                }`}>
                  {date.getDate()}
                </span>
              </div>
              
              {/* Daily Progress Bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>{totalHours.toFixed(1)}h</span>
                  <span>{goals.dailyHours}h</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1">
                  <div 
                    className={`h-1 rounded-full transition-all duration-300 ${
                      progress >= 100 ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Add Entry Button */}
            <button
              onClick={() => onAddEntry(date)}
              className="w-full p-2 border border-dashed border-slate-300 rounded text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-all mb-3"
            >
              <Plus className="w-4 h-4 mx-auto" />
            </button>

            {/* Time Entries */}
            <div className="space-y-2">
              {entries.map((entry) => (
                <TimeEntryCard
                  key={entry.id}
                  entry={entry}
                  clients={clients}
                  projects={projects}
                  onEdit={onEntryEdit}
                  onDuplicate={onEntryDuplicate}
                  onDelete={onEntryDelete}
                  compact={true}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Month View Component
const MonthView = ({ 
  dates, 
  currentDate,
  getEntriesForDate,
  getTotalHoursForDate,
  isToday,
  isCurrentMonth,
  onAddEntry,
  onEntryEdit,
  onEntryDuplicate,
  onEntryDelete,
  clients,
  projects 
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-4">
      {/* Month Grid Header */}
      <div className="grid grid-cols-7 gap-4">
        {weekDays.map((day) => (
          <div key={day} className="text-center font-medium text-slate-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-7 gap-4">
        {dates.map((date, index) => {
          const entries = getEntriesForDate(date);
          const totalHours = getTotalHoursForDate(date);
          const isCurrentDay = isToday(date);
          const isInCurrentMonth = isCurrentMonth(date);

          return (
            <div
              key={index}
              className={`border rounded-lg p-2 min-h-[120px] transition-all ${
                isCurrentDay 
                  ? 'border-blue-500 bg-blue-50' 
                  : isInCurrentMonth
                    ? 'border-slate-200 bg-white hover:border-slate-300'
                    : 'border-slate-100 bg-slate-50 text-slate-400'
              }`}
            >
              {/* Date Header */}
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${
                  isCurrentDay ? 'text-blue-700' : 
                  isInCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                }`}>
                  {date.getDate()}
                </span>
                {totalHours > 0 && (
                  <span className="text-xs text-slate-600">
                    {totalHours.toFixed(1)}h
                  </span>
                )}
              </div>

              {/* Add Entry Button */}
              <button
                onClick={() => onAddEntry(date)}
                className="w-full p-1 border border-dashed border-slate-300 rounded text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-all mb-2 text-xs"
              >
                <Plus className="w-3 h-3 mx-auto" />
              </button>

              {/* Time Entries as Cards */}
              <div className="space-y-1">
                {entries.slice(0, 2).map((entry) => (
                  <TimeEntryCard
                    key={entry.id}
                    entry={entry}
                    clients={clients}
                    projects={projects}
                    onEdit={onEntryEdit}
                    onDuplicate={onEntryDuplicate}
                    onDelete={onEntryDelete}
                    compact={true}
                  />
                ))}
                {entries.length > 2 && (
                  <button
                    onClick={() => setSelectedDate(date)}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 py-1"
                  >
                    +{entries.length - 2} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Date Detail Modal */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {getEntriesForDate(selectedDate).map((entry) => (
                <TimeEntryCard
                  key={entry.id}
                  entry={entry}
                  clients={clients}
                  projects={projects}
                  onEdit={onEntryEdit}
                  onDuplicate={onEntryDuplicate}
                  onDelete={onEntryDelete}
                  compact={false}
                />
              ))}
              {getEntriesForDate(selectedDate).length === 0 && (
                <p className="text-slate-500 text-center py-4">No entries for this day</p>
              )}
            </div>
            
            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  onAddEntry(selectedDate);
                  setSelectedDate(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Entry
              </button>
              <button
                onClick={() => setSelectedDate(null)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Calendar View Component
const CalendarView = ({ 
  timeEntries = [], 
  clients = [], 
  projects = [], 
  currentDate = new Date(), 
  viewMode = 'week', 
  onDateChange,
  onEntryEdit,
  onEntryDuplicate,
  onEntryDelete,
  onEntryCreate,
  goals = { dailyHours: 8, weeklyHours: 40 },
  className = ""
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  // Generate calendar dates based on view mode
  const generateCalendarDates = () => {
    const dates = [];
    const start = new Date(currentDate);
    
    if (viewMode === 'day') {
      dates.push(new Date(start));
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(start);
      startOfWeek.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
    } else if (viewMode === 'month') {
      const startOfMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      const endOfMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      const startOfCalendar = new Date(startOfMonth);
      startOfCalendar.setDate(startOfMonth.getDate() - startOfMonth.getDay());
      
      for (let i = 0; i < 42; i++) { // 6 weeks max
        const date = new Date(startOfCalendar);
        date.setDate(startOfCalendar.getDate() + i);
        dates.push(date);
        if (date >= endOfMonth && date.getDay() === 6) break;
      }
    }
    
    return dates;
  };

  const calendarDates = generateCalendarDates();

  // Helper functions
  const getEntriesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return timeEntries.filter(entry => entry.entry_date === dateStr);
  };

  const getTotalHoursForDate = (date) => {
    const entries = getEntriesForDate(date);
    return entries.reduce((total, entry) => total + (entry.duration_hours || 0), 0);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Navigation functions
  const handlePrevious = () => {
    if (onDateChange) {
      const newDate = new Date(currentDate);
      if (viewMode === 'day') newDate.setDate(newDate.getDate() - 1);
      else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
      else if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
      onDateChange(newDate);
    }
  };

  const handleNext = () => {
    if (onDateChange) {
      const newDate = new Date(currentDate);
      if (viewMode === 'day') newDate.setDate(newDate.getDate() + 1);
      else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
      else if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
      onDateChange(newDate);
    }
  };

  const handleToday = () => {
    if (onDateChange) {
      onDateChange(new Date());
    }
  };

  // Entry handling functions
  const handleAddEntry = (date) => {
    setSelectedDate(date);
    setShowNewEntryModal(true);
  };

  const handleEntryEdit = (entry) => {
    setEditingEntry(entry);
    setShowEditEntryModal(true);
  };

  const handleEntryCreate = (entryData) => {
    if (onEntryCreate) {
      onEntryCreate(entryData);
    }
  };

  const handleEntrySave = (entryId, entryData) => {
    if (onEntryEdit) {
      onEntryEdit(entryId, entryData);
    }
  };

  // Calculate progress for day view
  const calculateDayProgress = (date) => {
    const totalHours = getTotalHoursForDate(date);
    return {
      actual: totalHours,
      goal: goals.dailyHours,
      percentage: (totalHours / goals.dailyHours) * 100
    };
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-slate-200 ${className}`}>
      {/* Calendar Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-slate-800">
              {viewMode === 'day' && currentDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
              {viewMode === 'week' && `Week of ${currentDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}`}
              {viewMode === 'month' && currentDate.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevious}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleToday}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Today
            </button>
            
            <button
              onClick={handleNext}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="p-6">
        {viewMode === 'day' && (
          <DayView
            date={currentDate}
            entries={getEntriesForDate(currentDate)}
            progress={calculateDayProgress(currentDate)}
            onEntryEdit={handleEntryEdit}
            onEntryDuplicate={onEntryDuplicate}
            onEntryDelete={onEntryDelete}
            onAddEntry={handleAddEntry}
            clients={clients}
            projects={projects}
          />
        )}

        {viewMode === 'week' && (
          <WeekView
            dates={calendarDates}
            timeEntries={timeEntries}
            getEntriesForDate={getEntriesForDate}
            getTotalHoursForDate={getTotalHoursForDate}
            isToday={isToday}
            onAddEntry={handleAddEntry}
            onEntryEdit={handleEntryEdit}
            onEntryDuplicate={onEntryDuplicate}
            onEntryDelete={onEntryDelete}
            clients={clients}
            projects={projects}
            goals={goals}
          />
        )}

        {viewMode === 'month' && (
          <MonthView
            dates={calendarDates}
            currentDate={currentDate}
            getEntriesForDate={getEntriesForDate}
            getTotalHoursForDate={getTotalHoursForDate}
            isToday={isToday}
            isCurrentMonth={isCurrentMonth}
            onAddEntry={handleAddEntry}
            onEntryEdit={handleEntryEdit}
            onEntryDuplicate={onEntryDuplicate}
            onEntryDelete={onEntryDelete}
            clients={clients}
            projects={projects}
          />
        )}
      </div>

      {/* New Entry Modal */}
      {showNewEntryModal && (
        <NewEntryModal
          selectedDate={selectedDate}
          clients={clients}
          projects={projects}
          onClose={() => {
            setShowNewEntryModal(false);
            setSelectedDate(null);
          }}
          onCreateEntry={handleEntryCreate}
        />
      )}

      {/* Edit Entry Modal */}
      {showEditEntryModal && editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          clients={clients}
          projects={projects}
          onClose={() => {
            setShowEditEntryModal(false);
            setEditingEntry(null);
          }}
          onSave={handleEntrySave}
        />
      )}
    </div>
  );
};

export default CalendarView;